import { ColumnDef } from '@tanstack/react-table';
import { useParams, useSearch } from '@tanstack/react-router';
import { cn } from '@anytag/shared-utils';
import { useTikTokSpecialPermissions, useUserRoles } from '@anytag/auth';
import { Anchor, CheckBox, MediaPreview, TableCell } from '@anytag/shared-atoms';
import { InfluencerAvatar, TiktokSpecialPostStatus } from '@anytag/shared-molecules';
import { useGenerateInfluencerProfileLink, useQueryHelper, useSelectItems } from '@anytag/shared-hooks';
import { CampaignSocialMediaType, TikTokSpecialCampaignPost, TikTokSpecialCampaignPostStatus } from '@anytag/graphql';

export interface TikTokCampaignPostsListTableProps {
  posts: readonly TikTokSpecialCampaignPost[];
}

export const useTikTokCampaignPostsListTable = ({ posts }: TikTokCampaignPostsListTableProps) => {
  const searchParams = useSearch({ from: '/_private-routes/tiktok/campaigns/$id/posts' });
  const { t, navigate } = useQueryHelper();
  const params = useParams({ from: '/_private-routes/tiktok/campaigns/$id/posts' });
  const { isAdminStaff } = useUserRoles();
  const { generateProfileLink } = useGenerateInfluencerProfileLink({ disabled: !isAdminStaff }); // permissions logic not implemented on BE for tiktok special advertisers
  const { hideRejectInappropriatePostBtn } = useTikTokSpecialPermissions();

  const checkHasPostStatusControl = (post: Pick<TikTokSpecialCampaignPost, 'id' | 'status'>) =>
    !hideRejectInappropriatePostBtn &&
    post.id &&
    post.status &&
    [TikTokSpecialCampaignPostStatus.EFFECTIVE, TikTokSpecialCampaignPostStatus.WARNING].includes(post.status);

  const postsActions = useSelectItems<TikTokSpecialCampaignPost, keyof TikTokSpecialCampaignPost>({
    key: 'id',
    listItems: posts.reduce<TikTokSpecialCampaignPost[]>(
      (acc, curr) => (checkHasPostStatusControl(curr) ? [...acc, curr] : acc),
      []
    )
  });

  const { allItemIds, selectedItems, selectItem, toggleSelectAllItems } = postsActions;
  const hasSelectAllCheckBox = !hideRejectInappropriatePostBtn && allItemIds.length;
  const columns: ColumnDef<TikTokSpecialCampaignPost>[] = [
    {
      size: 40, // For multiple sticky columns
      accessorKey: 'selectAll',
      meta: { sticky: true, hidden: !hasSelectAllCheckBox },
      header: () => <CheckBox help={t('Select all')} onClick={toggleSelectAllItems} disabled={!allItemIds.length} />,
      cell: ({ row }) => {
        const isSelectedPost =
          selectedItems.findIndex((p) => p.id === row.original.id && p.influencer.id === row.original.influencer.id) !==
          -1;

        return checkHasPostStatusControl(row.original) ? (
          <CheckBox
            checked={isSelectedPost}
            help={t(isSelectedPost ? 'Unselect' : 'Select')}
            onClick={row.original.id ? () => selectItem(row.original.id) : undefined}
          />
        ) : (
          <div />
        );
      }
    },
    {
      size: 164,
      accessorKey: 'influencer',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { influencer } = row.original;

        return (
          <TableCell.Link
            label={influencer.name}
            className="flex min-w-50 items-center"
            {...generateProfileLink(influencer.id.toString())}
          >
            <InfluencerAvatar size="40px" src={influencer.avatar} asChild />
            <TableCell.Text className="ml-2 min-w-50" value={influencer.name} />
          </TableCell.Link>
        );
      }
    },
    {
      accessorKey: 'content',
      header: t<string>('HeaderColumn.Content'),
      cell: ({ row }) => {
        const postDetailsUrl = row.original.id
          ? {
              to: '/tiktok/campaigns/$id/posts/$postId',
              params: { id: params.id, postId: row.original.id?.toString() },
              search: searchParams
            }
          : undefined;

        const goToPostDetails = () => {
          if (postDetailsUrl) {
            navigate(postDetailsUrl);
          }
        };

        return (
          <div
            onClick={goToPostDetails}
            className={cn('flex items-center gap-2', { 'cursor-pointer': !!postDetailsUrl })}
          >
            <MediaPreview src={row.original.thumbNail} className="h-10 w-15" />
            <Anchor lines={2} className="w-max max-w-75" label={row.original.content || '-'} />
          </div>
        );
      }
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Post Status'),
      cell: ({ row }) => (row.original.status ? <TiktokSpecialPostStatus status={row.original.status} /> : '')
    },
    {
      accessorKey: 'revenuePerPost',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Revenue per Posting'),
      cell: ({ row }) => (
        <TableCell.Costs
          currency={row.original.currency}
          costPerPost={row.original.revenuePerPost}
          socialMedia={CampaignSocialMediaType.TIKTOK}
        />
      )
    },
    {
      accessorKey: 'postCode',
      header: t<string>('HeaderColumn.Post Code'),
      cell: ({ row }) => <TableCell.Text value={row.original.postCode || ''} className="min-w-20" />
    },
    {
      accessorKey: 'phoneNumber',
      meta: { hidden: !isAdminStaff },
      header: t<string>('HeaderColumn.Phone Number'),
      cell: ({ row }) => <TableCell.Text value={row.original.phoneNumber || ''} className="min-w-20" />
    },
    {
      accessorKey: 'postedDate',
      header: t<string>('HeaderColumn.Post date'),
      cell: ({ row }) => <TableCell.Date value={row.original.postedDate || ''} />
    }
  ];

  return { columns, postsActions };
};
